import { useState, useEffect, useCallback } from 'react'
import { cmsService } from '../../services/cmsService'
import { Field, Input, Textarea, SectionCard, PageHeader, SpinnerIcon } from '../../components/cms/shared'
import RichTextEditor from '../../components/cms/RichTextEditor'

const PAGE = 'contact'
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || 'https://tendercentrix.com'

const DEFAULTS = {
    hero: { title: 'Contact', description: 'For clarification, governance inquiries, and platform support.' },
    contactCard: {
        heading: 'Get in touch',
        email: 'support@tendercentrix.com',
        hoursLabel: 'Hours:',
        hours: 'Monday to Friday, 09:00–17:00 (UK time)',
        responseTimeLabel: 'Response Time:',
        responseTime: 'Within one business day',
        urgentNote: 'For urgent account or billing issues, please include "URGENT" in your subject line.',
    },
}

function initState() {
    return { hero: { saving: false, saved: false, error: null }, contactCard: { saving: false, saved: false, error: null } }
}

export default function ContactCmsPage() {
    const [content, setContent] = useState(DEFAULTS)
    const [pageLoading, setPageLoading] = useState(true)
    const [sectionState, setSectionState] = useState(initState())

    useEffect(() => {
        cmsService.getPage(PAGE)
            .then((res) => { if (res?.data) setContent({ ...DEFAULTS, ...res.data }) })
            .catch(() => {})
            .finally(() => setPageLoading(false))
    }, [])

    const setSaving = (section, state) =>
        setSectionState((prev) => ({ ...prev, [section]: { ...prev[section], ...state } }))

    const save = useCallback(async (section, payload) => {
        setSaving(section, { saving: true, saved: false, error: null })
        try {
            await cmsService.updateSection(PAGE, section, payload)
            setSaving(section, { saving: false, saved: true, error: null })
            setTimeout(() => setSaving(section, { saved: false }), 3000)
        } catch (err) {
            setSaving(section, { saving: false, saved: false, error: err.message || 'Save failed' })
        }
    }, [])

    if (pageLoading) return <div className="flex items-center justify-center py-24"><SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" /></div>

    return (
        <div className="space-y-6">
            <PageHeader
                title="Contact — Content Editor"
                description="Edit the Contact page hero and contact card details."
                liveUrl={`${FRONTEND_URL}/contact`}
            />

            <SectionCard title="Hero Banner" onSave={() => save('hero', content.hero)} {...sectionState.hero}>
                <Field label="Page Title"><Input value={content.hero.title} onChange={(v) => setContent((c) => ({ ...c, hero: { ...c.hero, title: v } }))} /></Field>
                <Field label="Description"><RichTextEditor value={content.hero.description} onChange={(v) => setContent((c) => ({ ...c, hero: { ...c.hero, description: v } }))} /></Field>
            </SectionCard>

            <SectionCard title="Contact Card" description="The email, hours, and urgent note shown on the page." onSave={() => save('contactCard', content.contactCard)} {...sectionState.contactCard}>
                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Heading"><Input value={content.contactCard.heading} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, heading: v } }))} /></Field>
                    <Field label="Email Address"><Input value={content.contactCard.email} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, email: v } }))} /></Field>
                    <Field label="Hours Label"><Input value={content.contactCard.hoursLabel} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, hoursLabel: v } }))} /></Field>
                    <Field label="Hours"><Input value={content.contactCard.hours} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, hours: v } }))} /></Field>
                    <Field label="Response Time Label"><Input value={content.contactCard.responseTimeLabel} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, responseTimeLabel: v } }))} /></Field>
                    <Field label="Response Time"><Input value={content.contactCard.responseTime} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, responseTime: v } }))} /></Field>
                </div>
                <Field label="Urgent Note"><RichTextEditor value={content.contactCard.urgentNote} onChange={(v) => setContent((c) => ({ ...c, contactCard: { ...c.contactCard, urgentNote: v } }))} /></Field>
            </SectionCard>
        </div>
    )
}
